import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Chip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { VerificationStatus } from 'api/file/interfaces/get-file-order-insights';
import OrderInsightsRiskIcon from './OrderInsightsRiskIcon';

type StatusRowProps = {
  label: string;
  value: VerificationStatus | boolean | string | undefined;
  index: number;
  isPlainText?: boolean;
  interpretBooleanAsText?: boolean;
  isPdf?: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 2),
    },
    striped: {
      backgroundColor: theme.palette.brand.feather50,
    },
    label: {
      fontWeight: 500,
      width: '25%',
    },
    value: {
      width: '50%',
      display: 'flex',
      justifyContent: 'flex-start',
    },
    chipBase: {
      fontWeight: 400,
      fontSize: pxToRem(12),
      lineHeight: pxToRem(18),
      letterSpacing: '0.16px',
      textAlign: 'center',
      verticalAlign: 'middle',
      paddingLeft: theme.spacing(1.25),
    },
    passed: {
      backgroundColor: '#E8F5E9',
      color: theme.palette.success.dark,
    },
    failed: {
      backgroundColor: '#FDECEA',
      color: theme.palette.error.light,
    },
    warning: {
      backgroundColor: theme.palette.agGrid.overrideBackground,
      color: theme.palette.warning.dark,
    },
    default: {
      backgroundColor: '#e0e0e0',
      color: '#000',
    },
  }),
);

const getChipProps = (value: string, classes: Record<string, string>) => {
  switch (value) {
    case 'Passed':
      return {
        className: `${classes.chipBase} ${classes.passed}`,
        icon: <OrderInsightsRiskIcon riskSeverity="Success" />,
      };
    case 'Failed':
      return {
        className: `${classes.chipBase} ${classes.failed}`,
        icon: <OrderInsightsRiskIcon riskSeverity="Alert" />,
      };
    case 'Warning':
      return {
        className: `${classes.chipBase} ${classes.warning}`,
        icon: <OrderInsightsRiskIcon riskSeverity="Warning" />,
      };
    default:
      return {
        className: `${classes.chipBase} ${classes.default}`,
        icon: undefined,
      };
  }
};

const StatusRow: React.FC<StatusRowProps> = ({
  label,
  value,
  index,
  isPlainText = false,
  interpretBooleanAsText = false,
  isPdf = false,
}: StatusRowProps) => {
  const classes = useStyles();
  const isEvenRow = index % 2 === 0;
  let displayValue = 'N/A';

  if (typeof value === 'boolean') {
    displayValue = interpretBooleanAsText ? (value ? 'Yes' : 'No') : `${value}`;
  } else if (typeof value === 'string') {
    displayValue = value.trim() || 'N/A';
  }
  const { className, icon } = getChipProps(displayValue, classes);

  return (
    <Box className={`${classes.row} ${isEvenRow ? classes.striped : ''}`}>
      <Typography className={classes.label}>{isPdf ? <strong>{label}</strong> : label}</Typography>
      <Box className={classes.value}>
        {isPlainText ? (
          <Typography>{displayValue}</Typography>
        ) : (
          <Chip label={displayValue} className={className} icon={icon} />
        )}
      </Box>
    </Box>
  );
};

export default StatusRow;
